<template>
  <div class="tour-general-info">
    <div
      v-for="{ name, title, titleTooltip, code, anchor, description } in items"
      :key="name"
      class="tour-general-info__item"
      :class="{ attending: code === 'attending' }"
    >
      <div class="font-h2" v-html="name"></div>

      <div class="tour-general-info__text-container" v-if="outOfPlaceNewView && code === 'attending'">
        <div class="tour-general-info__text font-semibold u-text-secondary"
            v-html="$t('serpDetail.generalInfo.outOfPlaces')"></div>
      </div>
      <div v-else
           class="tour-general-info__text-container">
        <div
          class="tour-general-info__text font-semibold u-text-secondary"
          v-html="title"
        ></div>
        <tooltip
          v-if="titleTooltip"
          class="tour-general-info__text-tooltip u-flex-shrink-0 u-text-secondary"
          @show="onTooltipShow(name);"
          @apply-hide="onTooltipHide(name);"
        >
          <template #content>
            <div
              v-html="titleTooltip"
            ></div>
          </template>
        </tooltip>
      </div>

      <div class="tour-general-info__description"
           v-if="description"
           v-html="description"
      />

      <div
        v-if="tour.id && offer.id && code === 'attending' && !outOfPlaceNewView"
        class="tour-general-info__participants"
        :data-test="offer.id"
      >
        <tour-participants :tour-id="tour.id" :offer-id="offer.id" @booking="$emit('booking', {'trackingLabel': 'Participants'})" />
      </div>

      <div class="tour-general-info__anchor_block">
        <anchor v-if="anchor" :to="anchor.id" class="tour-general-info__anchor">
          <span v-html="anchor.text" />
          <svg-inline tag="span" class="u-decor u-stroke-deep-current">
            <icon icon="dropdown" />
          </svg-inline>
        </anchor>
      </div>
    </div>
  </div>
</template>

<script>
import Anchor from "@/components/ui-kit/anchor/index.vue";
import TourParticipants from "@/pages/serp-detail/components/TourParticipants.vue";
import { axiosTour } from "@/services/scopes/axios-tour";
import SvgInline from "@/components/ui-kit/svg-inline/index.vue";
import Icon from "@/components/icons/index.vue";
import Tooltip from "@/components/ui-kit/tooltip/index.vue";
import {ytEvents} from "@/plugins/analytics";
import { abTestController } from "@/plugins/abtest";
import {
  getBookingOffers,
} from "@/assets/js/utils/bookingOffers";
import {
  findClosestElemBySelector
} from "@/assets/js/utils/findClosestElemBySelector";

export default {
  components: {
    Anchor,
    TourParticipants,
    SvgInline,
    Icon,
    Tooltip,
  },
  props: {
    tour: {
      type: Object,
      required: true,
    },
    offer: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      participants: [],
      tooltipStartShowTime: [],
      outOfPlaceNewView: false,
      isEventsInitiated: false,
    };
  },
  computed: {
    items() {
      const {
        tour: {
          comfort_data = {},
          activity_data = {},
          language = "",
          age_from,
          age_to,
          routes_link,
        },
      } = this;

      const countParticipants = this.participants.length;
      let items =  [
        {
          name: this.$t("serpDetail.generalInfo.attending"),
          title: this.$t("serpDetail.generalInfo.age", { countParticipants, age_from, age_to }),
          code: 'attending',
        },
        {
          name: activity_data.title,
          title: activity_data.title_select,
          titleTooltip: activity_data.title_select_tooltip,
          code: 'route',
          anchor: {
            text: this.$t("serpDetail.generalInfo.seeRoute"),
            id: "#tour-route",
          },
        },
        {
          name: this.$t("serpDetail.generalInfo.language"),
          title: language,
          code: 'language',
        },
        {
          name: comfort_data.title,
          title: comfort_data.title_select,
          titleTooltip: comfort_data.title_select_tooltip,
          code: 'allocation',
          anchor: {
            text: this.$t("serpDetail.generalInfo.seeAllocations"),
            id: "#tour-allocation",
          },
        },
        {
          name: this.$t("serpDetail.generalInfo.route"),
          title: routes_link,
          code: 'route',
        },
      ];

      if(this.tour.format_type == 'oneday') {
        items.splice(3,1);
      }
      return items;
    },
    params() {
      return [this.tour.id, this.offer.id];
    },
  },
  methods: {
    async getOffers() {
      this.abtestOutOfPlaceNewView();

      if (this.outOfPlaceNewView) {
        await this.getBookingOffers(this.tour).then((offers) => {
          this.offers = offers;
        });

        this.outOfPlaceNewView = this.offers.length === 1 && this.offer.custom;
      }
    },
    abtestOutOfPlaceNewView () {
      this.outOfPlaceNewView = abTestController.getSegment(16763) === "B";
    },
    async getParticipants(tourId, offerId) {

      if (tourId, offerId) {
        await axiosTour
          .getParticipants(tourId, offerId)
          .then(({ data: { data } }) =>
            this.setParticipants(Object.values(data), tourId, offerId)
          )
          .catch(() => this.setParticipants([], tourId, offerId));
      }
    },
    async setParticipants(participants, tourId, offerId) {
      if (tourId === this.tour.id && offerId === this.offer.id) {
        this.participants = participants;
      }
    },
    trackEvents(cat, name, lbl, props) {
      ytEvents.track(cat, name, lbl, props);
    },
    onTooltipShow(name) {
      this.trackEvents('Tour detail', 'Show description tooltip', name);
      this.tooltipStartShowTime[name] = Date.now();
    },
    onTooltipHide(name) {
      const tooltipShowTime = (Date.now() - this.tooltipStartShowTime[name]) / 1000;
      this.trackEvents('Tour detail', 'Hide description tooltip', name, {showTime: tooltipShowTime});
    },
    initLocationTooltipEvent() {
      if (!this.isEventsInitiated) {
        document.addEventListener('click', this.onBodyClick);
        this.isEventsInitiated = true;
      }
    },
    onBodyClick(e) {
      const moreLocationsBtn = this.findClosestElemBySelector(e.target, '.js-more-locations-btn');

      if (moreLocationsBtn) { // Если нажали на кнопку "ещё", то открываем или закрываем ближайший к кнопке тултип
        const tooltip = this.findClosestElemBySelector(e.target, '.js-routes-links')?.getElementsByClassName('js-more-locations-tooltip')[0];
        if (tooltip) {
          tooltip.classList.toggle('show');
          moreLocationsBtn.classList.toggle('show');
        }
      } else if (!this.findClosestElemBySelector(e.target, '.js-more-locations-tooltip')) { // Если нажали вне тултипа, то закрываем все тултипы с локациями
        const tooltips = document.getElementsByClassName('js-more-locations-tooltip');
        for (let i = 0; i < tooltips.length; i++) {
          tooltips[i].classList.remove('show');
        }

        const moreLocationsBtns = document.getElementsByClassName('js-more-locations-btn');
        for (let i = 0; i < moreLocationsBtns.length; i++) {
          moreLocationsBtns[i].classList.remove('show');
        }
      }
    },
    findClosestElemBySelector,
    getBookingOffers,
  },
  watch: {
    params: {
      deep: true,
      immediate: true,
      handler([tourId, offerId]) {
        this.getParticipants(tourId, offerId);
        this.getOffers();
      },
    },
    items: {
      handler() {
        this.initLocationTooltipEvent();
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.tour-general-info {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-gap: 32px 16px;

  @include mixin.size-md-only {
    grid-template-columns: repeat(1,minmax(0,1fr)) 31% 31%;
    grid-gap: 32px 20px;
    max-width: 725px;
  }

  @include mixin.size-xl-min {
    grid-template-columns: repeat(1,minmax(0,1fr)) 31% 31%;
    grid-gap: 12px 12px;
  }

  &__item {
    border: 1px solid #EEEEF2;
    box-shadow: 2px 2px 15px rgb(153 153 169 / 15%);
    border-radius: 12px;
    padding: 16px 20px 16px 20px;
    display: grid;
    grid-template-rows: 11px auto auto;

    &:first-child {
      grid-column-start: 1;
      grid-row-start: 1;
      grid-row-end: 3;

      .u-text-secondary {
        margin-top: 1px;
      }
    }

    &.attending {
      display: block;

      .tour-general-info__participants {
        height: 115px;

        @include mixin.size-sm {
          height: 77px;
        }
      }
    }

    .font-h2 {
      color: #828296;
      font-weight: 600;
      font-size: 13px;
    }
  }

  &__text-container {
    display: inline-flex;
  }

  &__text-tooltip {
    margin-top: 0.5em;
    margin-left: 0.2em;
  }

  &__text{
    margin-top: func.divide(8, 14) * 1em;
    font-size: 13px;
    font-weight: 600;
    color: #000;
    line-height: 17px;
  }

  &__anchor_block {
    height: 89%;
    display: flex;
    align-items: end;
  }

  &__anchor {
    margin-top: func.divide(12, 14) * 1em;
    display: flex;
    align-items: center;
    width: 100%;
    font-size: 13px !important;

    .svg-inline {
      transform: rotate(-90deg);
      width: 15px;
      margin-top: 2px;
    }
  }

  &__participants {
    grid-column: span 2;
  }

  &__description {
    font-size: 14px;
    line-height: 18px;
    color: #9999a9;
    font-weight: 600;
    letter-spacing: -.02em;
    padding-top: 12px;
  }
}

</style>
